
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from "@/components/ui/form";
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, Send } from 'lucide-react';

interface SendNewsletterFormProps {
  onSendNewsletter: (subject: string, content: string) => Promise<boolean>;
  isSending: boolean;
  subscriberCount: number;
  activeSubscriberCount: number;
}

interface FormValues {
  subject: string;
  content: string;
}

const SendNewsletterForm: React.FC<SendNewsletterFormProps> = ({
  onSendNewsletter,
  isSending,
  subscriberCount,
  activeSubscriberCount
}) => {
  const form = useForm<FormValues>({
    defaultValues: {
      subject: '',
      content: ''
    }
  });

  const onSubmit = async (data: FormValues) => {
    const success = await onSendNewsletter(data.subject, data.content);
    if (success) {
      form.reset();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Send Newsletter</CardTitle>
        <CardDescription>
          Send an email to {activeSubscriberCount} active subscribers out of {subscriberCount} total subscribers.
        </CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="subject"
              rules={{ required: "Subject is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subject</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="May Newsletter - IT Security Tips" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="content"
              rules={{ required: "Content is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Content (HTML)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="<h1>May Newsletter</h1><p>Dear subscribers,</p><p>Here are this month's top IT security tips...</p>" 
                      className="min-h-[200px]"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter>
            <Button 
              type="submit" 
              disabled={isSending}
              className="w-full sm:w-auto"
            >
              {isSending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="mr-2 h-4 w-4" />
                  Send Newsletter
                </>
              )}
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
};

export default SendNewsletterForm;
